import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, errText, downloadFile } from "../../../lib/api";
import { UploadSimple, FileText, Trash } from "@phosphor-icons/react";

export const DocumentsTab = ({ empId }) => {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("General");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const loadDocs = () => api.get(`/employees/${empId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { loadDocs(); }, [empId]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/employees/${empId}/documents`, fd);
      toast.success("Document uploaded");
      loadDocs();
    } catch (err) { toast.error(errText(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const delDoc = async (docId) => {
    await api.delete(`/documents/${docId}`);
    toast.success("Document deleted");
    loadDocs();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="document-category-select"
          className="min-h-[44px] px-3 rounded-lg border border-stone-300 bg-white">
          {["General", "ID Proof", "Offer Letter", "Contract", "Certificate", "Previous Company", "Other"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={upload} className="hidden" data-testid="document-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-document-button"
          className="tap-scale flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-white text-sm font-bold hover:bg-[#A63F07] transition-colors duration-200 disabled:opacity-60">
          <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : "Upload document"}
        </button>
        <span className="text-xs text-stone-400">PDF, images or Word · max 10 MB</span>
      </div>
      <div className="divide-y divide-stone-100">
        {docs.length === 0 && <p className="py-8 text-center text-sm text-stone-400">No documents uploaded yet.</p>}
        {docs.map((d) => (
          <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`document-row-${d.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <FileText size={22} weight="duotone" className="text-primary shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{d.original_filename}</div>
                <div className="text-xs text-stone-500">{d.category} · {d.size < 1024 ? `${d.size} B` : `${(d.size / 1024).toFixed(1)} KB`} · by {d.uploaded_by}</div>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => downloadFile(`/documents/${d.id}/download`, d.original_filename).catch((e) => toast.error(errText(e)))}
                data-testid={`download-document-${d.id}`} className="text-primary font-bold text-xs hover:underline">Download</button>
              <button onClick={() => delDoc(d.id)} data-testid={`delete-document-${d.id}`} className="text-stone-400 hover:text-red-600 transition-colors duration-200"><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
